"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import Link from "next/link";
import DisputeCard from "./DisputeCard";
import DisputeSidebar from "./DisputeSidebar";
import { useUmaContext } from "./UmaContext";
import { STATE_COLORS } from "../utils";
import { authFetch } from "@/lib/authFetch";
import { LayoutGridIcon } from "@/app/components/icons/LayoutGridIcon";
import { LayoutListIcon } from "@/app/components/icons/LayoutListIcon";
import { useT } from "@/hooks/useT";
import Image from "next/image";
import type { Dispute } from "../types";
import {
  decodeOutcome,
  getOutcomeColor,
  formatVolume,
  formatBond,
  truncateAddress,
  timeAgo,
} from "../utils";

type Tab = "active" | "upcoming";
type ViewMode = "grid" | "list";

// Tab definitions are pure data — labels come from i18n inside the
// component so they re-translate when the user switches language.
const TABS: { key: Tab; href: string; filter: (d: Dispute) => boolean }[] = [
  { key: "active",   href: "/uma/active",   filter: d => d.dvmStatus === "active" },
  { key: "upcoming", href: "/uma/upcoming", filter: d => d.dvmStatus === "upcoming" },
];

const PAGE_SIZE = 12;

const PREMIUM_INTERVAL = 45_000;   // 45s for premium users
const NORMAL_INTERVAL = 5 * 60_000; // 5 min for non-logged / non-premium

interface DisputesListProps {
  defaultTab?: Tab;
}

export default function DisputesList({ defaultTab = "active" }: DisputesListProps) {
  const t = useT();
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const tab = defaultTab;
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [currentPhase, setCurrentPhase] = useState<"commit" | "reveal">("commit");
  const { advanced, setAdvanced, setIsPremium } = useUmaContext();
  const { isLoggedIn: authenticated, isReady: authReady, login } = useAuth();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [viewMode, setViewModeState] = useState<ViewMode>("grid");
  const [viewModeLoaded, setViewModeLoaded] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('frenflow-uma-view-mode');
      if (saved === 'grid' || saved === 'list') setViewModeState(saved);
      else if (window.innerWidth < 768) setViewModeState('list');
    } catch {}
    setViewModeLoaded(true);
  }, []);

  const setViewMode = (mode: ViewMode) => {
    setViewModeState(mode);
    try { localStorage.setItem('frenflow-uma-view-mode', mode); } catch {}
  };

  const retryRef = useRef(0);

  const fetchDisputes = useCallback(async (isInitial = false) => {
    try {
      const res = await authFetch("/api/uma/disputes");
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        // If backend returned degraded (empty) response, retry up to 3 times with backoff
        if (json.meta?.degraded && json.data.length === 0 && retryRef.current < 3) {
          retryRef.current++;
          setTimeout(() => fetchDisputes(isInitial), retryRef.current * 5000);
          return false;
        }
        retryRef.current = 0;
        setDisputes(json.data);
      }
      if (json.meta?.currentPhase) {
        setCurrentPhase(json.meta.currentPhase);
      }
      return !!json.meta?.premium;
    } catch {
      return false;
    } finally {
      if (isInitial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetchDisputes(true).then(premium => {
      if (cancelled) return;
      setIsPremium(premium);
      const interval = premium ? PREMIUM_INTERVAL : NORMAL_INTERVAL;
      intervalRef.current = setInterval(() => {
        fetchDisputes();
      }, interval);
    });

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchDisputes, setIsPremium]);

  const currentTab = TABS.find(t => t.key === tab)!;

  // Deduplicate by pmSlug — same market can have multiple disputes (re-proposals).
  // Keep the most relevant: Disputed > Proposed > Settled/Resolved
  const STATE_PRIORITY: Record<string, number> = { Disputed: 0, Proposed: 1, Resolved: 2, Settled: 3 };
  const filtered = (() => {
    const all = disputes.filter(currentTab.filter);
    const bySlug = new Map<string, Dispute>();
    for (const d of all) {
      const key = d.pmSlug || d.id; // fallback to id if no slug
      const existing = bySlug.get(key);
      if (!existing) {
        bySlug.set(key, d);
      } else {
        const ePri = STATE_PRIORITY[existing.state] ?? 9;
        const dPri = STATE_PRIORITY[d.state] ?? 9;
        // When same priority: prefer entry with proposer data, then most recent
        const eHasData = existing.proposer ? 1 : 0;
        const dHasData = d.proposer ? 1 : 0;
        if (dPri < ePri || (dPri === ePri && (dHasData > eHasData || (dHasData === eHasData && (d.requestTime || 0) > (existing.requestTime || 0))))) {
          bySlug.set(key, d);
        }
      }
    }
    return Array.from(bySlug.values());
  })();

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const closeSidebar = useCallback(() => setSelectedId(null), []);

  return (
    <>
      {/* Login badge — mobile only (above tabs) — only show once auth state is confirmed */}
      {!authenticated && authReady && (
        <div className="flex justify-center -mt-2 mb-4 lg:hidden">
          <button
            onClick={() => login()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] text-[11px] font-bold bg-[#CCFF00] text-gray-900 hover:bg-[#b8e600] transition-colors"
          >
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            {t("uma.signInForFullAccess")}
          </button>
        </div>
      )}

      {/* Filter Tabs + Login badge (desktop) + Advanced — flex-wraps
          on narrow viewports: tabs scroll horizontally if needed, Grid
          /List stays on the first line, Simple/Advanced drops to a
          centered second line when there isn't room for everything. */}
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="flex gap-1 p-1 bg-gray-100/80 dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] min-w-0 overflow-x-auto scrollbar-hide">
          {TABS.map(tabDef => {
            const count = disputes.filter(tabDef.filter).length;
            return (
              <Link
                key={tabDef.key}
                href={tabDef.href}
                prefetch={false}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-[5px] text-sm font-medium whitespace-nowrap transition-colors ${
                  tab === tabDef.key
                    ? "bg-white dark:bg-[#0f0f12] text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                {tabDef.key === "active" && count > 0 && (
                  <span className="w-1.5 h-1.5 rounded-[5px] bg-emerald-500 animate-pulse" />
                )}
                {t(`uma.tab.${tabDef.key}`)}
              </Link>
            );
          })}
        </div>

        {/* Login badge — desktop inline — only show once auth state is confirmed */}
        {!authenticated && authReady && (
          <button
            onClick={() => login()}
            className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] text-[11px] font-bold bg-[#CCFF00] text-gray-900 hover:bg-[#b8e600] transition-colors"
          >
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            {t("uma.signInForFullAccess")}
          </button>
        )}

        {/* Grid / List Toggle — always mounted to reserve space
            (prevents CLS when viewModeLoaded resolves). */}
        <div
          className="flex gap-0.5 p-0.5 bg-gray-100/80 dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] flex-shrink-0"
          style={{ visibility: viewModeLoaded ? "visible" : "hidden" }}
        >
          <button
            onClick={() => setViewMode("grid")}
            className={`p-1.5 rounded-[3px] transition-colors ${viewMode === "grid" ? "bg-white dark:bg-[#0f0f12] text-gray-900 dark:text-white shadow-sm" : "text-gray-500"}`}
          >
            <LayoutGridIcon size={14} isHovered={false} />
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`p-1.5 rounded-[3px] transition-colors ${viewMode === "list" ? "bg-white dark:bg-[#0f0f12] text-gray-900 dark:text-white shadow-sm" : "text-gray-500"}`}
          >
            <LayoutListIcon size={14} isHovered={false} />
          </button>
        </div>
        {/* Simple/Advanced toggle — when the toolbar wraps it gets its
            own row and centers itself thanks to the `w-full sm:w-auto`
            + flex centering below the sm breakpoint. */}
        <div className="w-full sm:w-auto flex justify-center sm:justify-end order-last sm:order-none">
          <div className="flex gap-0.5 p-0.5 bg-gray-100/80 dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]">
            {(["Simple", "Advanced"] as const).map(mode => (
              <button
                key={mode}
                onClick={() => setAdvanced(mode === "Advanced")}
                className={`px-2.5 py-1.5 rounded-[5px] text-[11px] font-medium whitespace-nowrap transition-colors ${
                  (mode === "Advanced") === advanced
                    ? "bg-white dark:bg-[#0f0f12] text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                {mode === "Simple" ? t("uma.toggle.simple") : t("uma.toggle.advanced")}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-64 rounded-[5px] bg-gray-100/80 dark:bg-white/[0.04] animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            {tab === "active" ? t("uma.empty.active") : t("uma.empty.upcoming")}
          </p>
        </div>
      ) : (
        <>
          {viewMode === "grid" ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
              {paginated.map(d => (
                <DisputeCard
                  key={d.id}
                  dispute={d}
                  currentPhase={currentPhase}
                  isSelected={selectedId === d.id}
                  onClick={() => setSelectedId(d.id)}
                  advanced={advanced}
                />
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-white/[0.02] rounded-[5px] border border-gray-200 dark:border-white/[0.04] overflow-hidden">
              {/* Table header */}
              <div className="hidden md:grid md:grid-cols-12 gap-3 px-4 py-2.5 border-b border-gray-100 dark:border-white/[0.04] text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <div className="col-span-5">{t("uma.list.market")}</div>
                <div className="col-span-1 text-center">{t("uma.list.state")}</div>
                <div className="col-span-2 text-center">{t("uma.list.proposed")}</div>
                <div className="col-span-1 text-right">{t("uma.list.volume")}</div>
                <div className="col-span-1 text-right">{t("uma.list.bond")}</div>
                <div className="col-span-1 text-right">{t("uma.list.voters")}</div>
                <div className="col-span-1 text-right">{t("uma.list.time")}</div>
              </div>
              {/* Rows */}
              <div className="divide-y divide-gray-100 dark:divide-white/[0.04]">
                {paginated.map(d => (
                  <DisputeRow
                    key={d.id}
                    dispute={d}
                    isSelected={selectedId === d.id}
                    onClick={() => setSelectedId(d.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="text-xs font-medium px-3 py-1.5 rounded-[5px] bg-white/80 dark:bg-white/[0.04] backdrop-blur-xl text-gray-700 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-white/[0.06] transition-colors border border-gray-200 dark:border-[#1A1A1A]"
              >
                {t("uma.pagination.previous")}
              </button>
              <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                {page * PAGE_SIZE + 1}&ndash;{Math.min((page + 1) * PAGE_SIZE, filtered.length)} {t("uma.pagination.of")} {filtered.length}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="text-xs font-medium px-3 py-1.5 rounded-[5px] bg-white/80 dark:bg-white/[0.04] backdrop-blur-xl text-gray-700 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-white/[0.06] transition-colors border border-gray-200 dark:border-[#1A1A1A]"
              >
                {t("uma.pagination.next")}
              </button>
            </div>
          )}
        </>
      )}

      {/* Dispute Detail Sidebar */}
      {selectedId && (
        <DisputeSidebar disputeId={selectedId} onClose={closeSidebar} currentPhase={currentPhase} />
      )}
    </>
  );
}

// =============================================================================
// LIST VIEW ROW
// =============================================================================

function DisputeRow({ dispute: d, isSelected, onClick }: {
  dispute: Dispute;
  isSelected: boolean;
  onClick: () => void;
}) {
  const t = useT();
  const proposed = decodeOutcome(d.proposedPrice);
  const proposedColor = getOutcomeColor(d.proposedPrice);
  const stateColor = STATE_COLORS[d.state] || 'bg-gray-500/10 text-gray-500';
  const voterCount = d.voteSummary?.votersAmount || d.voteSummary?.committersCount || 0;
  const bond = formatBond(d.bond);
  const disputeTime = d.disputeTime || d.proposalTime || d.requestTime;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors ${
        isSelected ? 'bg-gray-50 dark:bg-[#18181D]' : ''
      }`}
    >
      {/* Mobile: stacked */}
      <div className="md:hidden space-y-2">
        <div className="flex items-start gap-3">
          {d.pmImage && (
            <Image src={d.pmImage} alt="" width={36} height={36} className="w-9 h-9 rounded-[5px] object-cover flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1">{d.pmTitle || d.question}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-[5px] ${stateColor}`}>{d.state}</span>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-[5px] ${proposedColor}`}>{proposed}</span>
              {d.pmVolume != null && <span className="text-[10px] text-gray-500">{formatVolume(d.pmVolume)}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop: table row */}
      <div className="hidden md:grid md:grid-cols-12 gap-3 items-center">
        {/* Market */}
        <div className="col-span-5 flex items-center gap-3 min-w-0">
          {d.pmImage ? (
            <Image src={d.pmImage} alt="" width={32} height={32} className="w-8 h-8 rounded-[5px] object-cover flex-shrink-0" />
          ) : (
            <div className="w-8 h-8 rounded-[5px] bg-gray-200 dark:bg-[#0f0f12] flex-shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{d.pmTitle || d.question}</p>
            {d.proposer && (
              <p className="text-[10px] text-gray-500 mt-0.5">
                {t("uma.list.by")} {d.proposerEns || truncateAddress(d.proposer)}
                {d.disputer && <> · {t("uma.list.disputedBy")} {d.disputerEns || truncateAddress(d.disputer)}</>}
              </p>
            )}
          </div>
        </div>

        {/* State */}
        <div className="col-span-1 text-center">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-[5px] ${stateColor}`}>
            {d.state}
          </span>
        </div>

        {/* Proposed Outcome */}
        <div className="col-span-2 text-center">
          <span className={`text-xs font-bold px-2 py-0.5 rounded-[5px] ${proposedColor}`}>
            {proposed}
          </span>
          {d.voteSummary && d.voteSummary.groups?.length > 0 && (() => {
            const totalVotes = d.voteSummary!.groups.reduce((s, g) => s + Number(g.totalVoteAmount || 0), 0);
            return (
              <div className="flex items-center gap-0.5 mt-1 justify-center">
                {d.voteSummary!.groups.slice(0, 3).map((g, i) => {
                  const pct = totalVotes > 0 ? (Number(g.totalVoteAmount) / totalVotes) * 100 : 0;
                  const color = getOutcomeColor(g.price);
                  return (
                    <div key={i} className="h-1 rounded-[5px]" style={{
                      width: `${Math.max(pct, 8)}%`,
                      minWidth: '8px',
                      maxWidth: '40px',
                      backgroundColor: color.includes('green') ? '#10b981'
                        : color.includes('red') ? '#ef4444' : '#6b7280',
                      opacity: 0.7,
                    }} />
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Volume */}
        <div className="col-span-1 text-right">
          <span className="text-xs font-medium text-gray-900 dark:text-white tabular-nums">
            {d.pmVolume != null ? formatVolume(d.pmVolume) : '—'}
          </span>
        </div>

        {/* Bond */}
        <div className="col-span-1 text-right">
          <span className="text-xs text-gray-500 tabular-nums">
            {bond || '—'}
          </span>
        </div>

        {/* Voters */}
        <div className="col-span-1 text-right">
          <span className="text-xs text-gray-500 tabular-nums">
            {voterCount > 0 ? voterCount : '—'}
          </span>
        </div>

        {/* Time */}
        <div className="col-span-1 text-right">
          <span className="text-[10px] text-gray-500">
            {disputeTime ? timeAgo(disputeTime) : '—'}
          </span>
        </div>
      </div>
    </button>
  );
}
